// src/screens/Admin/ActiveWalletManager.js
import React, { useEffect, useState } from 'react';
import './ActiveWalletManager.css';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import backIcon from '../../assets/backspace-icon.png';

function ActiveWalletManager() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchMatrix = async () => {
      try {
        setLoading(true);
        setError('');

        const res = await api.get('/admin/metrics/wallet-activations?network=mainnet');
        const data = res.data;

        // Compute total balance from user balances
        const totalBalance = (data.users || []).reduce(
          (sum, user) => sum + (user.balance || 0),
          0
        );

        setSummary({
          network: data.network,
          threshold: data.threshold,
          totalUsers: data.totalUsers,
          activeWalletUsers: data.activeWalletUsers,
          activeRate: data.activeRate,
          totalBalance: totalBalance,
        });

        setRows(data.users || []);
      } catch (err) {
        console.error('Failed to load wallet activation matrix:', err);
        setError('Failed to load active wallet data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchMatrix();
  }, []);

  const filteredRows = rows.filter((row) => {
    if (!search) return true;
    const term = search.toLowerCase();
    return (
      row.username?.toLowerCase().includes(term) ||
      row.displayName?.toLowerCase().includes(term) ||
      row.country?.toLowerCase().includes(term) ||
      row.userType?.toLowerCase().includes(term)
    );
  });

  return (
    <div className="awm-page">
      {/* Top bar */}
      <div className="awm-topbar">
        <img
          src={backIcon}
          alt="Back"
          className="awm-back-icon"
          onClick={() => navigate(-1)}
        />
        <h2 className="awm-title">Active Wallets (Mainnet)</h2>
      </div>

      <div className="awm-body">
        {/* Summary */}
        {summary && (
          <div className="awm-summary-row">
            <div className="awm-summary-card">
              <p className="awm-summary-label">Total Users</p>
              <p className="awm-summary-value">{summary.totalUsers}</p>
            </div>
            <div className="awm-summary-card">
              <p className="awm-summary-label">Active Wallets</p>
              <p className="awm-summary-value">{summary.activeWalletUsers}</p>
            </div>
            <div className="awm-summary-card">
              <p className="awm-summary-label">Activation Rate</p>
              <p className="awm-summary-value">
                {(summary.activeRate * 100).toFixed(2)}%
              </p>
            </div>
            <div className="awm-summary-card">
              <p className="awm-summary-label">Threshold</p>
              <p className="awm-summary-value">
                ≥ {summary.threshold} Pi
              </p>
            </div>
            <div className="awm-summary-card">
              <p className="awm-summary-label">Total Balance (Pi)</p>
              <p className="awm-summary-value">{summary.totalBalance?.toFixed(3)}</p>
            </div>
          </div>
        )}

        {/* Search + status */}
        <div className="awm-toolbar">
          <input
            type="text"
            className="awm-search-input"
            placeholder="Search by username, display name, country..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {summary && (
            <span className="awm-network-pill">
              Network: {summary.network}
            </span>
          )}
        </div>

        {/* Error / loading */}
        {error && <div className="awm-error">{error}</div>}
        {loading && <div className="awm-loading">Loading active wallets…</div>}

        {/* Table */}
        {!loading && !error && (
          <div className="awm-table-wrapper">
            {filteredRows.length === 0 ? (
              <div className="awm-empty">
                No users with active mainnet wallets found.
              </div>
            ) : (
              <table className="awm-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Display Name</th>
                    <th>Country</th>
                    <th>User Type</th>
                    <th>Deposit Total (Pi)</th>
                    <th>Balance (Pi)</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row.userId || row.username}>
                      <td>@{row.username}</td>
                      <td>{row.displayName || '—'}</td>
                      <td>{row.country || '—'}</td>
                      <td>{row.userType || '—'}</td>
                      <td>{Number(row.depositTotal || 0).toFixed(3)}</td>
                      <td>{Number(row.balance || 0).toFixed(3)}</td>
                      <td>
                        {row.createdAt
                          ? new Date(row.createdAt).toLocaleDateString()
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ActiveWalletManager;
